import { useArenaStore } from '../store'
import { DOMAIN_COLOR, selectCallout } from './wayfinding'

/**
 * Gaze / proximity building callout. When the player nears or looks toward one
 * of the named buildings, its name and one-line "what's inside" surface as a
 * subtle bottom-centre readout. Only ever the single nearest / most looked-at
 * building. Gated exactly like the rest of the HUD.
 */
export function BuildingCallout() {
  const isPlaying         = useArenaStore((s) => s.isPlaying)
  const isPaused          = useArenaStore((s) => s.isPaused)
  const activeInteraction = useArenaStore((s) => s.activeInteraction)
  const inspectorRunId    = useArenaStore((s) => s.inspectorRunId)
  const playerX           = useArenaStore((s) => s.playerX)
  const playerZ           = useArenaStore((s) => s.playerZ)
  const playerYaw         = useArenaStore((s) => s.playerYaw)

  if (!isPlaying || isPaused || activeInteraction || inspectorRunId) return null

  const pick = selectCallout(playerX, playerZ, playerYaw)
  if (!pick) return null

  const color = DOMAIN_COLOR[pick.building.domain] ?? 'rgba(245,235,224,0.8)'

  return (
    <div style={s.wrap} data-testid="building-callout">
      <span style={{ ...s.dot, background: color }} />
      <span style={s.name}>{pick.building.name}</span>
      {pick.building.blurb && (
        <>
          <span style={s.sep}>·</span>
          <span style={s.blurb}>{pick.building.blurb}</span>
        </>
      )}
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    position: 'fixed',
    bottom: '2.6rem',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    padding: '0.35rem 0.85rem',
    background: 'rgba(61,64,91,0.75)',
    border: '1px solid rgba(245,235,224,0.12)',
    borderRadius: '9999px',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    zIndex: 14,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    maxWidth: '90vw',
    overflow: 'hidden',
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: '0 0 4px rgba(0,0,0,0.4)',
  },
  name: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.82rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    color: '#f5ebe0',
  },
  sep: {
    color: 'rgba(245,235,224,0.35)',
    fontSize: '0.8rem',
  },
  blurb: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.76rem',
    fontWeight: 600,
    color: 'rgba(245,235,224,0.6)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}
